/**
 * AmenitiesEditor — toggle grid of amenities for a single trail.
 * Shared between the trail-admin per-trail status form (AdminPanel)
 * and the curator's catalog edit modal (CatalogManager).
 *
 * Storage convention: each amenity is a row in trail_attributes keyed
 * `amenity.<key>` with valueType="boolean" and value "true". Toggle on
 * → PUT; toggle off → DELETE (row existence is the source of truth).
 */

import { useEffect, useState } from "react";
import {
  getTrail,
  setTrailAttribute,
  deleteTrailAttribute,
  ApiError,
} from "../api";
import {
  AMENITIES,
  AMENITIES_HEADING,
  amenityKeyToAttribute,
} from "../amenities";

export function AmenitiesEditor({ slug }: { slug: string }) {
  const [enabled, setEnabled] = useState<Set<string> | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTrail(slug)
      .then((r) => {
        if (cancelled) return;
        const enabledKeys = new Set<string>();
        for (const attrKey of Object.keys(r.trail.attributes ?? {})) {
          if (attrKey.startsWith("amenity.")) {
            enabledKeys.add(attrKey.slice("amenity.".length));
          }
        }
        setEnabled(enabledKeys);
      })
      .catch(() => setEnabled(new Set()));
    return () => { cancelled = true; };
  }, [slug]);

  async function toggle(amenityKey: string) {
    if (!enabled || pendingKey) return;
    const wasOn = enabled.has(amenityKey);
    const optimistic = new Set(enabled);
    if (wasOn) optimistic.delete(amenityKey);
    else optimistic.add(amenityKey);
    setEnabled(optimistic);
    setPendingKey(amenityKey);
    setError(null);

    const attrKey = amenityKeyToAttribute(amenityKey);
    try {
      if (wasOn) {
        await deleteTrailAttribute(slug, attrKey);
      } else {
        await setTrailAttribute(slug, attrKey, "true", "boolean");
      }
    } catch (err) {
      // Roll back optimistic update.
      const reverted = new Set(enabled);
      setEnabled(reverted);
      setError(err instanceof ApiError ? err.message : "Failed to save.");
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <div className="amenities-editor">
      <h4>{AMENITIES_HEADING}</h4>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Tap any amenity you've got at the trailhead. Shows up on every rider's
        detail screen alongside your trail name.
      </p>
      <div className="amenities-grid">
        {AMENITIES.map((amenity) => {
          const isOn = enabled?.has(amenity.key) ?? false;
          const isPending = pendingKey === amenity.key;
          return (
            <button
              key={amenity.key}
              type="button"
              className={`amenity-chip ${isOn ? "on" : ""} ${isPending ? "pending" : ""}`}
              onClick={() => toggle(amenity.key)}
              disabled={enabled === null || isPending}
              aria-pressed={isOn}
            >
              <span className="amenity-icon">{amenity.icon}</span>
              <span className="amenity-label">{amenity.label}</span>
              <span className="amenity-check" aria-hidden>
                {isOn ? "✓" : ""}
              </span>
            </button>
          );
        })}
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
